import { Calendar, ExternalLink, Eye, Play, ThumbsUp, User, Youtube } from 'lucide-react'
import Image from 'next/image'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { youtubeDetails } from 'sprites-types/workflow/tools/youtubeDetails'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function YoutubeDetailsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(youtubeDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const videos = Array.isArray(result) ? result : [result]
  const video = videos[0] // Display first video, but preserve all data
  const title = video.title || 'Unknown Video'
  const channelTitle = video.channel || 'Unknown Channel'
  const description = video.description || ''
  const thumbnail = video.thumbnail || ''
  const publishedAt = video.publishedAt
  const duration = video.duration || ''
  const viewCount = video.viewCount
  const likeCount = video.likeCount
  const videoUrl = video.id ? `https://youtube.com/watch?v=${video.id}` : ''

  const formatNumber = (num: string | number) => {
    const n = typeof num === 'string' ? parseInt(num) : num
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return n.toLocaleString()
  }

  const formatDuration = (duration: string) => {
    if (!duration) return ''
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/)
    if (!match) return duration
    const hours = match[1] ? parseInt(match[1]) : 0
    const minutes = match[2] ? parseInt(match[2]) : 0
    const seconds = match[3] ? parseInt(match[3]) : 0

    if (hours > 0)
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      {/* Thumbnail */}
      {thumbnail && (
        <div className="relative">
          <Image
            src={thumbnail}
            alt={title}
            width={400}
            height={192}
            className="h-48 w-full object-cover"
          />
          {duration && (
            <div className="absolute bottom-2 right-2 rounded bg-black/80 px-2 py-1 text-xs text-white">
              {formatDuration(duration)}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        <div className="mb-3 flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-red-100 p-2">
              <Youtube className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 line-clamp-2 text-sm font-semibold text-gray-900">{title}</div>
            <div className="mb-2 flex items-center gap-1 text-xs text-gray-600">
              <User className="h-3 w-3" />
              <span>{channelTitle}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-3 flex items-center gap-4 text-xs text-gray-600">
          {viewCount && (
            <div className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              <span>{formatNumber(viewCount)} views</span>
            </div>
          )}
          {likeCount && (
            <div className="flex items-center gap-1">
              <ThumbsUp className="h-3 w-3" />
              <span>{formatNumber(likeCount)} likes</span>
            </div>
          )}
          {publishedAt && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(publishedAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Description Preview */}
        {description && (
          <div className="mb-3 line-clamp-3 text-xs text-gray-700">{description}</div>
        )}

        {/* Actions */}
        {videoUrl && (
          <div className="flex gap-2">
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700"
            >
              <Play className="h-3 w-3" />
              Watch on YouTube
            </a>
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

function downloadYoutubeDetails(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function youtubeDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(youtubeDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return ''
  }

  const result = parseResult.data

  const videos = Array.isArray(result) ? result : [result]
  const video = videos[0] // Display first video, but preserve all data
  const title = video.title || 'Unknown Video'
  const channelTitle = video.channel || 'Unknown Channel'
  const description = video.description || ''
  const publishedAt = video.publishedAt
  const viewCount = video.viewCount
  const likeCount = video.likeCount
  const videoUrl = video.id ? `https://youtube.com/watch?v=${video.id}` : ''

  return `## YouTube Video Details

**Title:** ${title}
**Channel:** ${channelTitle}
${publishedAt ? `**Published:** ${new Date(publishedAt).toLocaleDateString()}` : ''}
${viewCount ? `**Views:** ${Number(viewCount).toLocaleString()}` : ''}
${likeCount ? `**Likes:** ${Number(likeCount).toLocaleString()}` : ''}

${
  description
    ? `**Description:**
${description}`
    : ''
}

${videoUrl ? `[Watch on YouTube](${videoUrl})` : ''}

---
`
}

export const YoutubeDetailsDisplay = createWorkflowToolComponent(
  YoutubeDetailsDisplayComponent,
  downloadYoutubeDetails,
  youtubeDetailsToMarkdown
)
